import { MessagesSquare, PencilLine, UserRound, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { useInternalNotes } from '../../../../hooks/use-internal-notes';
import { useAppointments, usePendencies } from '../../hooks/use-panel-fixtures';
import { countUnread, useSupervisionSeenStore } from '../../supervision/supervision-seen-store';
import { SupervisionDrawer } from '../../supervision/SupervisionDrawer';
import { SupervisionStrip } from '../../supervision/SupervisionStrip';
import { useConversationSupervision } from '../../supervision/use-conversation-supervision';

import { LivingSummary } from './LivingSummary';
import { TabActions } from './TabActions';
import { TabCliente } from './TabCliente';
import { TabConversa } from './TabConversa';
import { TabNotas } from './TabNotas';

import type { ConversationDetail } from '../../../../lib/types';
import type { LucideIcon } from 'lucide-react';

type TabId = 'acoes' | 'cliente' | 'conversa' | 'notas';

/**
 * Painel híbrido — Brief 2A V2.3 Mudança #1 + r7b §6 Variants.
 *
 *   ┌──────────────────────────────┐
 *   │   LivingSummary (sticky)     │  ← sempre visível
 *   ├──────────────────────────────┤
 *   │  [⚡Ações·N][👤Cliente]…     │  ← Tablist (capability-gated + badge)
 *   ├──────────────────────────────┤
 *   │                              │
 *   │    TabPanel ativo            │
 *   │                              │
 *   └──────────────────────────────┘
 *
 * **Agent C Sprint Visual Brief 2A r7b — fidelidade canonical:**
 * Tabs canonical com ícones (zap/user-round/messages-square/pencil-line) + badge
 * canonical (.tab__badge DS) na aba Ações com contagem combinada
 * pending+unclear+appointments+pending-pendencies.
 *
 * Tablist segue role/aria de acessibilidade. Capability gating r7b §6.1:
 *   - Aba "Conversa" requer `conversation-sequences` (Team+).
 *     Solo variant não tem esta capability → aba oculta.
 *   - Abas Ações / Cliente / Notas são universais (sem gate).
 */
export function HybridPanel({
  conversation,
  openSupervision = false,
}: {
  conversation: ConversationDetail;
  /** `?supervisao=1` — chegou pelo aviso global: abre o painel de orientações ao montar. */
  openSupervision?: boolean;
}): JSX.Element {
  const hasConversaTab = useCapability('conversation-sequences');
  const [activeTab, setActiveTab] = useState<TabId>('acoes');

  // Supervisão visível ao operador (A-41): faixa acima do Resumo Vivo + painel deslizante.
  // Nada disto ocupa espaço quando não há supervisão nesta conversa.
  const supervision = useConversationSupervision(conversation.id);
  const lastSeen = useSupervisionSeenStore((s) => s.seen[conversation.id]);
  const markSeen = useSupervisionSeenStore((s) => s.markSeen);
  const unread = countUnread(supervision.messages, lastSeen);
  const [supervisionOpen, setSupervisionOpen] = useState(openSupervision);
  useEffect(() => {
    if (openSupervision) setSupervisionOpen(true);
  }, [openSupervision]);
  useEffect(() => {
    const last = supervision.messages[supervision.messages.length - 1];
    if (supervisionOpen && last !== undefined) markSeen(conversation.id, last.timestamp);
  }, [supervisionOpen, supervision.messages, conversation.id, markSeen]);

  /**
   * Badge da aba Ações — conta EXATAMENTE o que a aba mostra.
   *
   * Antes somava também os `CustomerCommitment` (ADR-105). Quando a seção deles saiu da
   * aba (spec `2026-08-14-aba-acoes-conversa-em-curso-design.md` §5.3), o badge teria
   * continuado contando linhas que não aparecem em lugar nenhum — número sem lastro na
   * tela, e o operador clicando na aba para procurar algo que não está lá.
   *
   * `usePendencies` já devolve só o que está vivo (open/in_progress/postponed), então
   * `.length` é a contagem certa — sem refiltrar aqui, que seria um segundo lugar
   * decidindo o que é "vivo" e divergindo do primeiro.
   */
  const { items: appointments } = useAppointments(conversation);
  const { items: pendencies } = usePendencies(conversation);
  const acoesCount = appointments.length + pendencies.length;

  // Contagem da aba Notas (pedido do TL, 08/Out/2026): sem ela, de outra aba nada dizia
  // que a conversa tinha notas. Conta TODAS — o servidor não registra leitura de nota, e
  // «não lidas» exigiria tabela + endpoint novos. Mesma `queryKey` do `TabNotas`: cache
  // compartilhado, nenhum pedido a mais ao abrir a aba.
  const notasCount = useInternalNotes(conversation.id).data.length;

  // Se o tab ativo foi removido por capability (Solo variant sem Conversa),
  // recair para a primeira tab disponível.
  useEffect(() => {
    if (activeTab === 'conversa' && !hasConversaTab) {
      setActiveTab('acoes');
    }
  }, [activeTab, hasConversaTab]);

  // `neutral`: o contador de Notas não usa o destaque das Ações — nota é contexto, não
  // trabalho pendente; dois destaques iguais ensinariam o olho a ignorar os dois.
  const visibleTabs: {
    id: TabId;
    label: string;
    Icon: LucideIcon;
    badge?: number;
    neutral?: boolean;
  }[] = [
    { id: 'acoes', label: 'Ações', Icon: Zap, badge: acoesCount },
    { id: 'cliente', label: 'Cliente', Icon: UserRound },
    ...(hasConversaTab
      ? [{ id: 'conversa' as const, label: 'Conversa', Icon: MessagesSquare }]
      : []),
    { id: 'notas', label: 'Notas', Icon: PencilLine, badge: notasCount, neutral: true },
  ];

  return (
    <aside className="relative w-[360px] shrink-0 border-l border-border-subtle bg-surface flex flex-col overflow-auto">
      <SupervisionStrip
        watchers={supervision.watchers}
        messages={supervision.messages}
        unread={unread}
        onOpen={() => setSupervisionOpen(true)}
      />
      {supervisionOpen && (
        <SupervisionDrawer
          watchers={supervision.watchers}
          messages={supervision.messages}
          onClose={() => setSupervisionOpen(false)}
        />
      )}
      <LivingSummary conversation={conversation} />

      {/* DS canonical .tablist + modifier --hybrid: os 4 tabs do painel (Ações/
          Cliente/Conversa/Notas) dividem a largura igualmente e encolhem (sem
          overflow horizontal — bug "Notas fora da tela"). Ícones lucide
          canônicos (zap/user-round/messages-square/pencil-line). */}
      <div className="tablist tablist--hybrid" role="tablist">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-label={tab.label}
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            /* DS canonical .tab + .tab--selected (largura/encolhimento via .tablist--hybrid) */
            className={activeTab === tab.id ? 'tab tab--selected' : 'tab'}
          >
            <tab.Icon className="icon" aria-hidden />
            <span className="tab__label">{tab.label}</span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                className={tab.neutral ? 'tab__badge tab__badge--neutral' : 'tab__badge'}
                aria-hidden
                data-testid={`tab-badge-${tab.id}`}
              >
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* DS canonical .tabpanel — replaces flex-1 overflow-auto Tailwind */}
      <div className="tabpanel flex-1" role="tabpanel" aria-label={activeTab}>
        {activeTab === 'acoes' && <TabActions conversation={conversation} />}
        {activeTab === 'cliente' && <TabCliente conversation={conversation} />}
        {activeTab === 'conversa' && hasConversaTab && <TabConversa conversation={conversation} />}
        {activeTab === 'notas' && <TabNotas conversation={conversation} />}
      </div>
    </aside>
  );
}
